/**
 * QueueWizard — create/edit a queue in the SAME wizard style as event
 * creation (route `queues.create`, param id=<queueId> to edit): a phase track
 * across the top, one focused step at a time, Back/Continue footer, review
 * step at the end. Steps:
 *   1 Type & journey (basics + seed THIS queue's journey from a starter, a
 *   copy of another queue's journey, or blank) · 2 Queue flow (the embedded
 *   journey on the screen+message diagram canvas: pages in customer order —
 *   incl. optional pages — plus messages, timers and decisions, added via the
 *   ⊕ insert picker; page nodes open the page designer) ·
 *   3 Opening hours · 4 Staffing · 5 Resources · 6 Services ·
 *   7 Review & publish · 8 Live (after publishing).
 * EMBEDDED JOURNEYS (2026-07-21 operator decision): the shared Queue Journeys
 * library is retired — every queue OWNS its journey (`queue.journey`), seeded
 * once and edited freely per queue. Legacy queues backfill from their flowId
 * (folding the old pageOverrides in) on first edit.
 * Required fields (marked *): queue name, location, a seeded journey,
 * ≥1 opening-hours set, ≥1 service. The Review & publish step
 * shows a completeness checklist and gates the Publish button on it.
 * Publishing AWAITS createQueue + updateQueue + setQueueStatus(open) — no fake
 * interactivity — then reveals the Live tab. Queues are never locked/unlocked:
 * services, personnel and resources can be added/removed on a running queue.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { alpha, Btn, CopyableUrl, Dropdown, Icon, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { NumberField } from "../../lib/NumberField";
import { useWfResource } from "../workforce/useWorkforce";
import { useStaff } from "../staff/useStaff";
import { useResources } from "../resources/useResources";
import { useServices, ServicesStore } from "../services/services-data";
import { useLocations } from "../locations/useLocations";
import {
  listQueues, listQueueTypes, listQueueFlows, createQueue, updateQueue, setQueueStatus,
  getQueueBoard, sendQueueMessage,
  type QueueSummary, type QueueType, type QueueFlowRecord, type QueueHours, type QueueStaffRef, type QueueServiceSel,
  type QueueBoard, type QueueEntry, type QueueListPage,
} from "./queues-api";
import { qfMigrate, qfDefaultFlow, qfTypeDefaultFlow, qfMkPage, qfRemove, type QFFlow, type QFStep } from "./qf-model";
import { QueueFlowBuilder } from "./QueueFlowBuilder";
import { QPAGE_META, type QPageId } from "./qp-pages";
import { Q_DAYS, TIME_OPTS, hmLabel12, qHoursLabel } from "./queue-hours";
import { publicUrl, useTenantPublicConfig } from "../../lib/public-url";
import { QrCode, downloadQrPng } from "../../shell/QrCode";

import { useT } from "@v0y4ge/i18n-runtime";
const STEPS = ["Type & journey", "Queue flow", "Opening hours", "Staffing", "Resources", "Services", "Review & publish"];

/** Insert an optional page at its journey position: Pre-booking FIRST, the
 *  Location picker right after the Services page. */
function insertPageAt(steps: QFStep[], pg: QFStep, p: QPageId): QFStep[] {
  const at = p === "prebook" ? 0 : Math.max(0, steps.findIndex((s) => s.kind === "page" && s.page === "services") + 1);
  return [...steps.slice(0, at), pg, ...steps.slice(at)];
}

function Req() {
  const t = useT(); return <span aria-label={t('queues.wizard.ariaLabel')} style={{ color: T.red1, marginLeft: 3 }}>*</span>; }

const label: CSSProperties = { fontSize: 12, fontWeight: 600, color: T.n1, marginBottom: 5 };
const inputStyle: CSSProperties = { width: "100%", minHeight: 42, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, fontSize: 14, fontFamily: T.font, boxSizing: "border-box" };

function StepHead({ title, sub, req }: { title: string; sub: string; req?: boolean }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <h2 style={{ fontSize: 19, fontWeight: 700, color: T.n1, margin: 0 }}>{title}{req ? <Req /> : null}</h2>
      <p style={{ fontSize: 13, color: T.soft, margin: "6px 0 0", lineHeight: 1.5 }}>{sub}</p>
    </div>
  );
}

export default function QueueWizard() {
  const t = useT();
  const nav = useNav();
  const editId = (nav.params?.id ?? null) as string | null;
  const { data: queuesData } = useWfResource<QueueListPage>("queues|all", () => listQueues(null));
  const { data: typesData } = useWfResource<{ types: QueueType[] }>("qtypes", () => listQueueTypes());
  const { data: flowsData } = useWfResource<{ flows: QueueFlowRecord[] }>("qflows", () => listQueueFlows()); // legacy-backfill + copy resolution only
  const { members } = useStaff();
  const { resources } = useResources();
  const { locations } = useLocations();
  useServices();

  const types = typesData?.types ?? [];
  const flows = flowsData?.flows ?? [];
  const editing = editId ? (queuesData?.data ?? []).find((q) => q.id === editId) ?? null : null;

  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [ticketPrefix, setTicketPrefix] = useState("");
  const [typeId, setTypeId] = useState("quick");
  const [locationId, setLocationId] = useState("");
  const [sla, setSla] = useState(10);
  const [hours, setHours] = useState<QueueHours[]>([{ days: [0, 1, 2, 3, 4], start: "09:00", end: "17:00" }]);
  const [staff, setStaff] = useState<QueueStaffRef[]>([]);
  const [needed, setNeeded] = useState(2);
  const [svcSel, setSvcSel] = useState<QueueServiceSel[]>([]);
  const [resSel, setResSel] = useState<string[]>([]);
  const [svcDrag, setSvcDrag] = useState<number | null>(null);
  const [svcOver, setSvcOver] = useState<number | null>(null);
  // THIS QUEUE'S embedded journey — seeded once on Type & journey, then owned
  // and edited per queue (autosaved). No shared library.
  const [journey, setJourney] = useState<QFFlow | null>(null);
  const [journeySource, setJourneySource] = useState<string>("");
  // Queue hero image — tops every page of the queue flow (data URL / URL).
  // Empty = brand gradient. Same uploader UX as the event wizard's hero.
  const [heroImage, setHeroImage] = useState<string>("");
  const [heroDrag, setHeroDrag] = useState(false); // ui-local — drag-over highlight
  const [copyPick, setCopyPick] = useState(false); // "Copy another queue's journey" modal (ui-local)
  const [published, setPublished] = useState<QueueSummary | null>(null); // set after a successful publish → reveals the Live tab (step 8)
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  // Autosave: the queue persists as you configure. Create mode makes a CLOSED
  // draft when you leave Type & basics; every later change PATCHes it
  // (debounced, awaited). Edit mode autosaves the existing queue the same way.
  const [draftId, setDraftId] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const lastSavedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!editing || hydrated) return;
    // Legacy queues (flowId, no embedded journey) backfill from the flow record
    // — wait for the flows list before hydrating.
    if (!editing.journey?.steps?.length && editing.flowId && !flowsData) return;
    setName(editing.name); setTypeId(editing.typeId); setLocationId(editing.locationId);
    setTicketPrefix(editing.ticketPrefix ?? "");
    setSla(editing.slaTargetMins); setHours(JSON.parse(JSON.stringify(editing.hours ?? [])));
    setStaff([...(editing.assignedStaff ?? [])]); setNeeded(editing.needed);
    // Selection when saved; else map legacy service NAMES onto catalog ids.
    if (editing.serviceSelection?.length) setSvcSel([...editing.serviceSelection]);
    else {
      const store = ServicesStore.services() as { id?: string; name?: string }[];
      setSvcSel((editing.services ?? []).map((n) => { const hit = store.find((s) => s.name === n); return hit?.id ? { type: "service" as const, id: hit.id } : null; }).filter(Boolean) as QueueServiceSel[]);
    }
    setResSel([...(editing.resources ?? [])]);
    setHeroImage(editing.heroImage ?? "");
    // Embedded journey — or one-time backfill: copy the legacy flow's steps and
    // fold the old per-queue overrides in; the copy persists on next autosave.
    if (editing.journey?.steps?.length) {
      setJourney(qfMigrate(editing.journey));
      setJourneySource(editing.journeySource || "This queue's journey");
    } else {
      const f = editing.flowId ? (flowsData?.flows ?? []).find((x) => x.id === editing.flowId) : null;
      let steps = qfMigrate(f ? { trigger: f.trigger, steps: JSON.parse(JSON.stringify(f.steps)) } : qfDefaultFlow()).steps;
      const ov = editing.pageOverrides ?? {}; const ovc = editing.pageOverrideCfgs ?? {};
      (["prebook", "locations"] as QPageId[]).forEach((p) => {
        const ids: string[] = [];
        const collect = (arr: QFStep[]) => arr.forEach((s) => { if (s.kind === "page" && s.page === p) ids.push(s.id); else if (s.kind === "decision") { collect(s.yes); collect(s.no); } });
        collect(steps);
        if (ov[p] === false) ids.forEach((id) => { steps = qfRemove(steps, id); });
        if (ov[p] === true && !ids.length) steps = insertPageAt(steps, qfMkPage(p, ovc[p]), p);
      });
      setJourney({ trigger: { event: "screen_services" }, steps });
      setJourneySource(f ? `Copied from “${f.name}”` : "This queue's journey");
    }
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, hydrated, flowsData]);

  /* ── Catalog-backed service selection (per queue, ordered, live groups) ─ */
  const catCategories = ServicesStore.categories() as { id: string; name: string; hue: string }[];
  const catServices = ServicesStore.services() as { id: string; name: string; category?: string | null; listedDuration?: string }[];
  const selHas = (type: "category" | "service", id: string) => svcSel.some((x) => x.type === type && x.id === id);
  const selAdd = (type: "category" | "service", id: string) => setSvcSel((cur) => cur.some((x) => x.type === type && x.id === id) ? cur : [...cur, { type, id }]);
  const selRemove = (i: number) => setSvcSel((cur) => cur.filter((_, xi) => xi !== i));
  const selMove = (i: number, d: number) => setSvcSel((cur) => { const j = i + d; if (j < 0 || j >= cur.length) return cur; const next = cur.slice(); const [it] = next.splice(i, 1); next.splice(j, 0, it); return next; });
  const selDropOn = (i: number) => setSvcSel((cur) => { if (svcDrag == null || svcDrag === i) return cur; const next = cur.slice(); const [it] = next.splice(svcDrag, 1); next.splice(i > svcDrag ? i - 1 : i, 0, it); return next; });
  /** Resolve the ordered selection → flat service names (category = its
   *  catalog services, live). Also what legacy `services` persists as. */
  const resolvedServiceNames = useMemo(() => {
    const names: string[] = [];
    svcSel.forEach((it) => {
      if (it.type === "category") catServices.forEach((s) => { if (s.category === it.id && s.name && !names.includes(s.name)) names.push(s.name); });
      else { const s = catServices.find((x) => x.id === it.id); if (s?.name && !names.includes(s.name)) names.push(s.name); }
    });
    return names;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svcSel, catServices.length]);
  const resourceNames = useMemo(() => {
    const fromStore = (resources ?? []).map((r: { name?: string }) => r.name).filter(Boolean) as string[];
    return [...new Set([...resSel, ...fromStore])];
  }, [resources, resSel]);

  const addableStaff = members.filter((m) => !staff.some((s) => s.id === m.id || s.name === m.name));
  const type = types.find((t) => t.id === typeId);
  /** THIS QUEUE'S journey pages (top-level + decision branches). */
  const pageSteps = useMemo(() => {
    const out: { id: string; page: QPageId; cfg: Record<string, unknown> }[] = [];
    const walk = (arr: QFStep[]) => arr.forEach((s) => {
      if (s.kind === "page") out.push({ id: s.id, page: s.page, cfg: s.cfg });
      if (s.kind === "decision") { walk(s.yes); walk(s.no); }
    });
    walk(journey?.steps ?? []);
    return out;
  }, [journey]);

  /** Apply page designs… (moved) — page design now happens INSIDE the embedded
   *  QueueFlowBuilder canvas (click a page node), which round-trips through
   *  onFlow → setJourney → autosave. */

  /** Optional journey pages — plain per-queue toggles now: ticking adds or
   *  removes the page on THIS queue's journey (no override concept). */
  const optionalPages: QPageId[] = ["prebook", "locations"];
  const journeyHas = (page: QPageId) => pageSteps.some((s) => s.page === page);
  const togglePage = (page: QPageId) => setJourney((j) => {
    if (!j) return j;
    const ids = pageSteps.filter((s) => s.page === page).map((s) => s.id);
    let steps = j.steps;
    if (ids.length) ids.forEach((id) => { steps = qfRemove(steps, id); });
    else steps = insertPageAt(steps, qfMkPage(page), page);
    return { ...j, steps };
  });

  /** Journey seeding (Type & journey step) — one-time starting points. */
  const reseedGuard = () => !journey || window.confirm("Replace this queue's journey? Its current pages, messages and flow edits will be lost.");
  const seedStarter = (t: QueueType) => {
    if (!reseedGuard()) return;
    setTypeId(t.id); setSla(t.presets.slaTargetMins);
    setJourney(qfMigrate(qfTypeDefaultFlow(t.id)));
    setJourneySource(`${t.label} starter`);
  };
  const seedBlank = () => {
    if (!reseedGuard()) return;
    // Blank reference (operator 2026-07-21): ONLY the Services page — the user adds the rest.
    setJourney({ trigger: { event: "screen_services" }, steps: [qfMkPage("services")] });
    setJourneySource("Started blank");
  };
  const journeyOfQueue = (q: QueueSummary): QFFlow | null => {
    if (q.journey?.steps?.length) return qfMigrate(JSON.parse(JSON.stringify(q.journey)));
    const f = q.flowId ? flows.find((x) => x.id === q.flowId) : null;
    return f ? qfMigrate({ trigger: f.trigger, steps: JSON.parse(JSON.stringify(f.steps)) }) : null;
  };
  const seedCopy = (q: QueueSummary) => {
    const j = journeyOfQueue(q);
    if (!j || !reseedGuard()) { setCopyPick(false); return; }
    setTypeId(q.typeId); setJourney(j); setJourneySource(`Copied from “${q.name}”`);
    setCopyPick(false);
  };
  const copyableQueues = (queuesData?.data ?? []).filter((q) => q.id !== editId && q.id !== draftId && (q.journey?.steps?.length || q.flowId));

  /** Flow stats for the Messaging & automation row. */
  const flowStats = useMemo(() => {
    let messages = 0, decisions = 0, waits = 0;
    const walk = (arr: QFStep[]) => arr.forEach((s) => { if (s.kind === "comms" || s.kind === "survey") messages++; else if (s.kind === "wait") waits++; else if (s.kind === "decision") { decisions++; walk(s.yes); walk(s.no); } });
    walk(journey?.steps ?? []);
    return { messages, decisions, waits };
  }, [journey]);

  /** Required-field completeness — surfaced on Review & publish, gates Publish/Save. */
  const reqChecks: { label: string; ok: boolean; step: number }[] = [
    { label: "Queue name", ok: !!name.trim(), step: 0 },
    { label: "Location", ok: !!locationId, step: 0 },
    { label: "Journey — starting point picked", ok: !!journey, step: 0 },
    { label: "Opening hours — at least one set", ok: hours.length > 0 && hours.every((h) => h.days.length > 0), step: 2 },
    { label: "Services — at least one", ok: resolvedServiceNames.length > 0, step: 5 },
  ];

  // The Live tab (step 8) exists for any LIVE queue: just published, or editing an open one.
  const liveQueue = published ?? (editing && editing.status === "open" ? editing : null);

  const buildConfig = () => ({ name: name.trim(), ticketPrefix: ticketPrefix.trim(), slaTargetMins: sla, needed, journey, journeySource, heroImage: heroImage || null, flowId: null, hours, assignedStaff: staff, services: resolvedServiceNames, serviceSelection: svcSel, resources: resSel });

  /** Create the CLOSED draft the first time we leave Type & basics (create mode). */
  const ensureDraft = async (): Promise<string | null> => {
    if (editing) return editing.id;
    if (draftId) return draftId;
    if (!name.trim()) return null;
    // A queue is OWNED by its location's branch company, and `queues.write` is
    // resolved against that location, so a create with no location can never
    // succeed -- the enforcer denies it 403 ("scope resolution failed"). The
    // name-autosave below used to fire this the moment a name was typed, so
    // every wizard run showed the operator a permission error before the
    // location select was ever touched.
    if (!locationId) return null;
    const locName = locations.find((l: { id: string; name: string }) => l.id === locationId)?.name;
    const created = await createQueue({ name: name.trim(), typeId, locationId, locationName: locName, services: resolvedServiceNames, flowId: null, slaTargetMins: sla, ticketPrefix: ticketPrefix.trim() });
    setDraftId(created.id);
    lastSavedRef.current = JSON.stringify(buildConfig());
    return created.id;
  };

  // Debounced autosave — PATCHes the draft / edited queue as fields change.
  const autosaveTarget = published ? null : editing ? (hydrated ? editing.id : null) : draftId;
  // Events-wizard-style autosave (F-75 paradigm): in create mode the CLOSED
  // draft is created automatically as soon as the queue has a name — no need
  // to leave step 1 — so every later keystroke persists via the PATCH below.
  useEffect(() => {
    if (editing || draftId || published || !name.trim() || !locationId) return;
    const t = setTimeout(() => {
      setSaveState("saving");
      ensureDraft().then(() => setSaveState("saved")).catch(() => setSaveState("error"));
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, draftId, published, name, locationId]);
  useEffect(() => {
    if (!autosaveTarget) return;
    const key = JSON.stringify(buildConfig());
    if (lastSavedRef.current === null) { lastSavedRef.current = key; return; } // baseline after hydrate
    if (key === lastSavedRef.current) return;
    const t = setTimeout(async () => {
      try {
        setSaveState("saving");
        await updateQueue(autosaveTarget, JSON.parse(key));
        lastSavedRef.current = key;
        setSaveState("saved");
      } catch {
        // Self-heal (create mode): the draft can vanish under the preview
        // harness (in-memory store reset / org switch mid-wizard) — recreate
        // it and re-apply the full config instead of failing forever.
        if (!editing) {
          try {
            const locName = locations.find((l: { id: string; name: string }) => l.id === locationId)?.name;
            const created = await createQueue({ name: name.trim() || "Untitled queue", typeId, locationId, locationName: locName, services: resolvedServiceNames, flowId: null, slaTargetMins: sla, ticketPrefix: ticketPrefix.trim() });
            await updateQueue(created.id, JSON.parse(key));
            setDraftId(created.id);
            lastSavedRef.current = key;
            setSaveState("saved");
            return;
          } catch { /* fall through to the error state */ }
        }
        setSaveState("error");
      }
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autosaveTarget, name, ticketPrefix, sla, needed, journey, journeySource, heroImage, hours, staff, svcSel, resSel, resolvedServiceNames]);

  const stepValid = (): string | null => {
    if (step === 0 && !name.trim()) return "Give the queue a name.";
    if (step === 0 && !journey) return "Pick a starting point for the customer journey.";
    if (step === 2 && hours.some((h) => h.days.length === 0)) return "Every opening-hours row needs at least one day.";
    return null;
  };
  const next = async () => {
    const v = stepValid(); if (v) { setErr(v); return; } setErr(null);
    if (step === 0 && !editing && !draftId && !published && name.trim()) {
      try { setSaveState("saving"); await ensureDraft(); setSaveState("saved"); }
      catch (e) { setSaveState("error"); setErr(e instanceof Error ? e.message : String(e)); return; }
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => { setErr(null); setStep((s) => Math.max(0, s - 1)); };
  const jump = (i: number) => { // free back-and-forth between steps
    setErr(null);
    if (!editing && !draftId && !published && name.trim()) { setSaveState("saving"); ensureDraft().then(() => setSaveState("saved")).catch(() => setSaveState("error")); }
    setStep(i);
  };

  const finish = async () => {
    const missing = reqChecks.filter((c) => !c.ok);
    if (missing.length) { setErr(`Complete the required fields first: ${missing.map((m) => m.label.split(" — ")[0]).join(", ")}.`); return; }
    setSaving(true); setErr(null);
    try {
      const config = buildConfig();
      if (editing) {
        await updateQueue(editing.id, config);
        toast({ tone: "success", title: "Queue updated", subtitle: `"${name.trim()}" configuration saved${editing.status === "open" ? " — changes apply to the running queue immediately" : ""}.` });
        nav.go("queues");
      } else {
        let id = draftId;
        if (!id) {
          const locName = locations.find((l: { id: string; name: string }) => l.id === locationId)?.name;
          const created = await createQueue({ name: name.trim(), typeId, locationId, locationName: locName, services: resolvedServiceNames, flowId: null, slaTargetMins: sla, ticketPrefix: ticketPrefix.trim() });
          id = created.id;
          setDraftId(id);
        }
        let updated: QueueSummary;
        try {
          updated = await updateQueue(id, config);
        } catch {
          // The draft vanished (preview-store reset / org switch) — recreate and publish that.
          const locName2 = locations.find((l: { id: string; name: string }) => l.id === locationId)?.name;
          const created2 = await createQueue({ name: name.trim(), typeId, locationId, locationName: locName2, services: resolvedServiceNames, flowId: null, slaTargetMins: sla, ticketPrefix: ticketPrefix.trim() });
          id = created2.id;
          setDraftId(id);
          updated = await updateQueue(id, config);
        }
        lastSavedRef.current = JSON.stringify(config);
        await setQueueStatus(id, "open");
        toast({ tone: "success", title: "Queue published", subtitle: `"${name.trim()}" is live and taking customers.` });
        setPublished(updated);
        setSaving(false);
        setStep(STEPS.length); // → Live tab
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  };

  const patchHours = (i: number, patch: Partial<QueueHours>) => setHours((cur) => cur.map((h, hi) => hi === i ? { ...h, ...patch } : h));
  const toggleDay = (i: number, d: number) => setHours((cur) => cur.map((h, hi) => {
    if (hi !== i) return h;
    const days = h.days.includes(d) ? h.days.filter((x) => x !== d) : [...h.days, d].sort((a, b) => a - b);
    return { ...h, days };
  }));

  const body = () => {
    if (step === 0) return (
      <>
        <StepHead title={t('queues.wizard.title3')} sub={t('queues.wizard.sub')} />
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 14 }}>
          <div><div style={label}>{t('queues.wizard.text8')}<Req /></div><input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('queues.wizard.placeholder')} required aria-required="true" style={inputStyle} data-testid="qwizard-name-input" autoFocus={!editing} /></div>
          <div>
            <div style={label}>{t('queues.wizard.text9')}<Req /></div>
            <Dropdown
              options={locations.map((l: { id: string; name: string }) => ({ value: l.id, label: l.name }))}
              value={locationId || null}
              onChange={(v) => setLocationId(v)}
              placeholder={locations.length ? t('queues.wizard.text10') : t('queues.wizard.text11')}
              disabled={!!editing}
              aria-label={t('queues.wizard.text9')}
              testid="qwizard-location-select"
              style={{ gap: 0 }}
            />
          </div>
        </div>
        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, maxWidth: 560 }}>
          <div>
            <div style={label}>{t('queues.wizard.text12')}</div>
            <input value={ticketPrefix} onChange={(e) => setTicketPrefix(e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 4))} placeholder={t('queues.wizard.placeholder2')} maxLength={4} style={inputStyle} data-testid="qwizard-prefix-input" />
            <div style={{ fontSize: 11.5, color: T.soft, marginTop: 5, lineHeight: 1.5 }}>{t('queues.wizard.text13')}{" "}{ticketPrefix ? t('queues.wizard.text14', { ticketPrefix }) : t('queues.wizard.text15')}{" "}{t('queues.wizard.text16')}</div>
          </div>
          <div>
            <div style={label}>{t('queues.wizard.text17')}</div>
            <NumberField value={sla} min={1} onChange={setSla} ariaLabel={t('queues.wizard.ariaLabel2')} style={inputStyle} />
          </div>
        </div>
        <div style={{ marginTop: 14, maxWidth: 560 }}>
          <div style={label}>{t('queues.wizard.text18')}</div>
          <div style={{ fontSize: 11.5, color: T.soft, margin: "2px 0 8px", lineHeight: 1.5 }}>{t('queues.wizard.text19')}</div>
          <div style={{ display: "flex", gap: 12, alignItems: "stretch" }}>
            <div data-testid="qwizard-hero-preview" style={{ width: 220, aspectRatio: "16 / 9", borderRadius: 8, background: heroImage ? `url(${heroImage}) center/cover` : "linear-gradient(135deg, #75ADFA, #5142C7)", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 22, fontWeight: 800, flexShrink: 0 }}>
              {!heroImage ? (name.trim() ? name.trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase() : "Q") : null}
              {heroImage ? <button onClick={() => setHeroImage("")} aria-label={t('queues.wizard.ariaLabel3')} data-testid="qwizard-hero-remove-btn" style={{ position: "absolute", top: 6, right: 6, width: 24, height: 24, borderRadius: 999, border: "none", background: "rgba(0,0,0,0.55)", color: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.x size={12}/></button> : null}
            </div>
            <label htmlFor="qhero-file-input" onDragOver={(e) => { e.preventDefault(); setHeroDrag(true); }} onDragLeave={() => setHeroDrag(false)}
              onDrop={(e) => { e.preventDefault(); setHeroDrag(false); const f = e.dataTransfer.files?.[0]; if (f && f.type.startsWith("image/")) { const rd = new FileReader(); rd.onload = () => setHeroImage(rd.result as string); rd.readAsDataURL(f); } }}
              style={{ flex: 1, border: `1px dashed ${heroDrag ? T.p1 : T.n4}`, background: heroDrag ? T.p4 : "transparent", borderRadius: 8, padding: 20, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 12.5, color: T.soft, cursor: "pointer", textAlign: "center" }}>
              <Icon.upload size={20} />
              <div>{heroImage ? t('queues.wizard.text20') : t('queues.wizard.text21')}{" "}<span style={{ color: T.p1, fontWeight: 600 }}>{t('queues.wizard.text22')}</span></div>
              <div style={{ fontSize: 11 }}>{t('queues.wizard.text23')}</div>
              <input id="qhero-file-input" type="file" accept="image/*" data-testid="qwizard-hero-file-input" onChange={(e) => { const f = e.target.files?.[0]; if (f && f.type.startsWith("image/")) { const rd = new FileReader(); rd.onload = () => setHeroImage(rd.result as string); rd.readAsDataURL(f); } e.target.value = ""; }} style={{ display: "none" }} />
            </label>
          </div>
        </div>
        <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{t('queues.wizard.text24')}<Req /></div>
          <p style={{ fontSize: 12.5, color: T.soft, margin: "5px 0 12px", lineHeight: 1.5 }}>{t('queues.wizard.text25')}</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, maxWidth: 640 }}>
            {types.map((ty) => {
              const on = journeySource === `${ty.label} starter`;
              return (
                <button key={ty.id} onClick={() => seedStarter(ty)} aria-pressed={on} data-testid="qwizard-seed-starter" data-id={ty.id}
                  style={{ textAlign: "left", padding: "13px 14px", borderRadius: 10, border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p4 : T.white, cursor: "pointer", fontFamily: T.font }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{ty.label} {t('queues.wizard.text26')}</div>
                  <div style={{ fontSize: 11.5, color: T.soft, marginTop: 2, lineHeight: 1.45 }}>{ty.blurb}</div>
                </button>
              );
            })}
            <button onClick={() => setCopyPick(true)} aria-pressed={journeySource.startsWith("Copied from")} data-testid="qwizard-seed-copy"
              style={{ textAlign: "left", padding: "13px 14px", borderRadius: 10, border: `1.5px solid ${journeySource.startsWith("Copied from") ? T.p1 : T.border}`, background: journeySource.startsWith("Copied from") ? T.p4 : T.white, cursor: "pointer", fontFamily: T.font }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t('queues.wizard.text27')}</div>
              <div style={{ fontSize: 11.5, color: T.soft, marginTop: 2, lineHeight: 1.45 }}>{t('queues.wizard.text28')}</div>
            </button>
            <button onClick={seedBlank} aria-pressed={journeySource === "Started blank"} data-testid="qwizard-seed-blank"
              style={{ textAlign: "left", padding: "13px 14px", borderRadius: 10, border: journeySource === "Started blank" ? `1.5px solid ${T.p1}` : `1.5px dashed ${T.n4}`, background: journeySource === "Started blank" ? T.p4 : T.white, cursor: "pointer", fontFamily: T.font }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t('queues.wizard.text29')}</div>
              <div style={{ fontSize: 11.5, color: T.soft, marginTop: 2, lineHeight: 1.45 }}>{t('queues.wizard.text30')}</div>
            </button>
          </div>
          {journey ? (
            <div style={{ marginTop: 12, fontSize: 12.5, color: T.n2, padding: "10px 12px", background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8 }}>
              <b>{journeySource || "Journey seeded"}</b> — {pageSteps.length} {t('queues.wizard.text31')}{" "}{pageSteps.length === 1 ? "" : t('queues.wizard.text32')} · {flowStats.messages} {t('queues.wizard.text33')}{" "}{flowStats.messages === 1 ? "" : t('queues.wizard.text32')}{" "}{t('queues.wizard.text34')}{" "}<b>{t('queues.wizard.text2')}</b> {t('queues.wizard.text35')}</div>
          ) : null}
        </div>
      </>
    );
    if (step === 1) {
      if (!journey) return (
        <>
          <StepHead req title={t('queues.wizard.title4')} sub={t('queues.wizard.sub2')} />
          <div style={{ padding: "22px 18px", border: `1.5px dashed ${T.n4}`, borderRadius: 10, fontSize: 13, color: T.soft, maxWidth: 560 }}>
            {t('queues.wizard.text36')}{" "}<button onClick={() => jump(0)} style={{ border: "none", background: "transparent", color: T.p1, fontWeight: 700, fontSize: 13, fontFamily: T.font, cursor: "pointer", padding: 0 }}>{t('queues.wizard.text')}</button> {t('queues.wizard.text37')}</div>
        </>
      );
      return (
        <>
          <StepHead title={t('queues.wizard.title4')} sub={t('queues.wizard.sub3')} />
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{t('queues.wizard.text38')}</span>
              {journeySource ? <Pill tone="info">{journeySource}</Pill> : null}
              <span style={{ fontSize: 11.5, color: T.soft }}>{flowStats.messages} {t('queues.wizard.text33')}{" "}{flowStats.messages === 1 ? "" : t('queues.wizard.text32')} · {flowStats.decisions} {t('queues.wizard.text39')}{" "}{flowStats.decisions === 1 ? "" : t('queues.wizard.text32')} · {flowStats.waits} {t('queues.wizard.text40')}{" "}{flowStats.waits === 1 ? "" : t('queues.wizard.text32')}</span>
              <span style={{ flex: 1 }}/>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 14 }}>
                {optionalPages.map((p) => {
                  const on = journeyHas(p);
                  return (
                    <label key={p} data-testid="qwizard-optional-page" data-id={p} title={QPAGE_META[p].sub} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: T.n1 }}>
                      <input type="checkbox" checked={on} onChange={() => togglePage(p)} aria-label={/* TODO(i18n): replace template-literal with t('queues.wizard.ariaLabel4', { ... }) */`${QPAGE_META[p].label} page`} style={{ width: 14, height: 14, accentColor: T.p1, cursor: "inherit" }} />
                      {QPAGE_META[p].label}
                    </label>
                  );
                })}
              </span>
            </div>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden", background: T.canvas }}>
              <QueueFlowBuilder flow={journey} onFlow={(nextFlow) => setJourney(nextFlow)} height={560} heroImage={heroImage || undefined} />
            </div>
            <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5 }}>{t('queues.wizard.text41')}{" "}<b>{t('queues.wizard.text42')}</b>.</div>
          </div>
        </>
      );
    }
    if (step === 2) return (
      <>
        <StepHead req title={t('queues.wizard.title5')} sub={t('queues.wizard.sub4')} />
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {hours.map((h, i) => (
            <div key={i} data-testid="qwizard-hours-row" style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                {Q_DAYS.map((d, di) => {
                  const on = h.days.includes(di);
                  return (
                    <button key={d} onClick={() => toggleDay(i, di)} aria-pressed={on} data-testid={`qwizard-day-${d.toLowerCase()}`}
                      style={{ width: 44, height: 36, borderRadius: 8, border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p1 : T.white, color: on ? T.white : T.n2, fontSize: 12, fontWeight: 700, fontFamily: T.font, cursor: "pointer" }}>
                      {d}
                    </button>
                  );
                })}
                <span style={{ flex: 1 }}/>
                <Btn variant="secondary" onClick={() => setHours((cur) => cur.filter((_, hi) => hi !== i))}>{t('queues.wizard.text43')}</Btn>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, maxWidth: 420 }}>
                <Dropdown
                  options={TIME_OPTS.map((v) => ({ value: v, label: hmLabel12(v) }))}
                  value={h.start}
                  onChange={(v) => patchHours(i, { start: v })}
                  aria-label={t('queues.wizard.ariaLabel5')}
                  testid="qwizard-hours-start"
                  style={{ gap: 0, width: 148 }}
                />
                <span style={{ fontSize: 13, color: T.soft }}>{t('queues.wizard.text44')}</span>
                <Dropdown
                  options={TIME_OPTS.filter((v) => v > h.start).map((v) => ({ value: v, label: hmLabel12(v) }))}
                  value={h.end}
                  onChange={(v) => patchHours(i, { end: v })}
                  aria-label={t('queues.wizard.ariaLabel6')}
                  testid="qwizard-hours-end"
                  style={{ gap: 0, width: 148 }}
                />
              </div>
            </div>
          ))}
          <div><Btn variant="secondary" icon={<Icon.plus size={13}/>} onClick={() => setHours((cur) => [...cur, { days: [], start: "09:00", end: "17:00" }])} testid="qwizard-add-hours-btn">{t('queues.wizard.text45')}</Btn></div>
          <div style={{ fontSize: 12.5, color: T.n2, padding: "10px 12px", background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8 }}><b>{t('queues.wizard.text46')}</b> {qHoursLabel(hours)}</div>
        </div>
      </>
    );
    if (step === 3) return (
      <>
        <StepHead title={t('queues.wizard.title6')} sub={t('queues.wizard.sub5')} />
        <div style={{ maxWidth: 520, display: "flex", flexDirection: "column", gap: 10 }}>
          {staff.map((s) => (
            <div key={s.id} data-testid="qwizard-staff-row" data-id={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", border: `1px solid ${T.border}`, borderRadius: 9 }}>
              <span style={{ width: 30, height: 30, borderRadius: 999, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 700, flexShrink: 0 }}>{s.name.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase()}</span>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: T.n1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</span>
              {s.role ? <span style={{ fontSize: 12, color: T.soft }}>{s.role}</span> : null}
              <Btn variant="secondary" onClick={() => setStaff((cur) => cur.filter((x) => x.id !== s.id))}>{t('queues.wizard.text43')}</Btn>
            </div>
          ))}
          {/* Picker, not a field: it stays on its placeholder and each pick appends
              a row, so `value` is always null. */}
          <Dropdown
            options={addableStaff.map((m) => ({ value: m.id, label: m.name }))}
            value={null}
            onChange={(v) => { const m = addableStaff.find((x) => x.id === v); if (m) setStaff((cur) => [...cur, { id: m.id, name: m.name, role: (m as { title?: string }).title || null }]); }}
            placeholder={t('queues.wizard.text47')}
            aria-label={t('queues.wizard.ariaLabel7')}
            testid="qwizard-add-staff-select"
            style={{ gap: 0 }}
          />
          <div style={{ maxWidth: 240 }}>
            <div style={label}>{t('queues.wizard.text48')}</div>
            <NumberField value={needed} min={1} onChange={setNeeded} ariaLabel={t('queues.wizard.ariaLabel8')} style={inputStyle} />
          </div>
        </div>
      </>
    );
    if (step === 4) {
      const addableRes = resourceNames.filter((r) => !resSel.includes(r));
      return (
        <>
          <StepHead title={t('queues.wizard.title7')} sub={t('queues.wizard.sub6')} />
          <div style={{ maxWidth: 520, display: "flex", flexDirection: "column", gap: 10 }}>
            {resSel.map((r) => (
              <div key={r} data-testid="qwizard-resource-row" data-id={r} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", border: `1px solid ${T.border}`, borderRadius: 9 }}>
                <span style={{ width: 30, height: 30, borderRadius: 8, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11.5, fontWeight: 700, flexShrink: 0 }}>{r.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase()}</span>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: T.n1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r}</span>
                <Btn variant="secondary" onClick={() => setResSel((cur) => cur.filter((x) => x !== r))}>{t('queues.wizard.text43')}</Btn>
              </div>
            ))}
            <Dropdown
              options={addableRes.map((r) => ({ value: r, label: r }))}
              value={null}
              onChange={(r) => { if (r) setResSel((cur) => cur.includes(r) ? cur : [...cur, r]); }}
              placeholder={t('queues.wizard.text49')}
              aria-label={t('queues.wizard.ariaLabel9')}
              testid="qwizard-add-resource-select"
              style={{ gap: 0 }}
            />
            {resourceNames.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft }}>{t('queues.wizard.text50')}</span> : null}
            {type && !type.presets.resourceTracking ? <div style={{ fontSize: 12, color: T.soft }}>{type.label} {t('queues.wizard.text51')}</div> : null}
          </div>
        </>
      );
    }
    if (step === 5) {
      const uncategorized = catServices.filter((s) => !s.category || !catCategories.some((cc) => cc.id === s.category));
      const svcRow = (s: { id: string; name: string; listedDuration?: string }) => {
        const on = selHas("service", s.id);
        return (
          <div key={s.id} data-testid="qwizard-catalog-service" data-id={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", border: `1px solid ${T.border}`, borderRadius: 8, background: T.white }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</span>
            {s.listedDuration ? <span style={{ fontSize: 11.5, color: T.soft, flexShrink: 0 }}>{s.listedDuration}</span> : null}
            <Btn variant="secondary" size="sm" disabled={on} onClick={() => selAdd("service", s.id)} testid="qwizard-add-service-btn">{on ? t('queues.wizard.text52') : t('queues.wizard.text53')}</Btn>
          </div>
        );
      };
      const selLabel = (it: QueueServiceSel): { name: string; sub: string; hue: string | null } => {
        if (it.type === "category") {
          const cc = catCategories.find((x) => x.id === it.id);
          const n = catServices.filter((s) => s.category === it.id).length;
          return { name: cc?.name ?? "Missing category", sub: `Category · all ${n} service${n === 1 ? "" : "s"} — updates with your catalog`, hue: cc?.hue ?? null };
        }
        const s = catServices.find((x) => x.id === it.id);
        return { name: s?.name ?? "Missing service", sub: s?.listedDuration ? `Service · ${s.listedDuration}` : "Service", hue: null };
      };
      return (
        <>
          <StepHead req title={t('queues.wizard.title8')} sub={t('queues.wizard.sub7')} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, alignItems: "start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: ".05em" }}>{t('queues.wizard.text54')}</div>
              {catCategories.map((cc) => {
                const inCat = catServices.filter((s) => s.category === cc.id);
                const on = selHas("category", cc.id);
                return (
                  <div key={cc.id} data-testid="qwizard-catalog-category" data-id={cc.id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 999, background: cc.hue, flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{cc.name}</span>
                      <Btn variant="secondary" size="sm" disabled={on} onClick={() => selAdd("category", cc.id)} testid="qwizard-add-category-btn">{on ? t('queues.wizard.text52') : t('queues.wizard.text55')}</Btn>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5, paddingLeft: 18 }}>
                      {inCat.map(svcRow)}
                      {inCat.length === 0 ? <span style={{ fontSize: 12, color: T.soft }}>{t('queues.wizard.text56')}</span> : null}
                    </div>
                  </div>
                );
              })}
              {uncategorized.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{t('queues.wizard.text57')}</span>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5, paddingLeft: 18 }}>{uncategorized.map(svcRow)}</div>
                </div>
              ) : null}
              {catCategories.length === 0 && catServices.length === 0 ? <span style={{ fontSize: 12.5, color: T.soft }}>{t('queues.wizard.text58')}</span> : null}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, position: "sticky", top: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: ".05em" }}>{t('queues.wizard.text59')}{svcSel.length ? /* TODO(i18n): replace template-literal with t('queues.wizard.text60', { ... }) */`${resolvedServiceNames.length} service${resolvedServiceNames.length === 1 ? "" : "s"}` : t('queues.wizard.text61')}</div>
              {svcSel.map((it, i) => {
                const info = selLabel(it);
                return (
                  <div key={`${it.type}:${it.id}`} draggable data-testid="qwizard-selected-service" data-id={it.id}
                    onDragStart={() => setSvcDrag(i)}
                    onDragEnd={() => { setSvcDrag(null); setSvcOver(null); }}
                    onDragOver={(e) => { e.preventDefault(); setSvcOver(i); }}
                    onDrop={() => { selDropOn(i); setSvcDrag(null); setSvcOver(null); }}
                    style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", border: `1px solid ${svcOver === i ? T.p1 : T.border}`, borderRadius: 9, background: it.type === "category" ? T.p4 : T.white, cursor: "grab", opacity: svcDrag === i ? .5 : 1 }}>
                    <span aria-hidden style={{ color: T.n4, fontSize: 13, lineHeight: 1, flexShrink: 0, letterSpacing: 1 }}>⋮⋮</span>
                    {info.hue ? <span style={{ width: 10, height: 10, borderRadius: 999, background: info.hue, flexShrink: 0 }} /> : null}
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{info.name}</span>
                      <span style={{ display: "block", fontSize: 11, color: T.soft }}>{info.sub}</span>
                    </span>
                    <span style={{ display: "inline-flex", gap: 4, flexShrink: 0 }}>
                      <Btn variant="ghost" size="sm" disabled={i === 0} onClick={() => selMove(i, -1)}><Icon.chev dir="up" size={11}/></Btn>
                      <Btn variant="ghost" size="sm" disabled={i === svcSel.length - 1} onClick={() => selMove(i, +1)}><Icon.chev size={11}/></Btn>
                      <Btn variant="ghost" size="sm" onClick={() => selRemove(i)}><Icon.x size={11}/></Btn>
                    </span>
                  </div>
                );
              })}
              {svcSel.length === 0 ? <div style={{ padding: "18px 14px", border: `1.5px dashed ${T.n4}`, borderRadius: 10, fontSize: 12.5, color: T.soft, textAlign: "center" }}>{t('queues.wizard.text62')}</div> : null}
              {svcSel.some((it) => it.type === "category") ? <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5 }}>{t('queues.wizard.text63')}</div> : null}
            </div>
          </div>
        </>
      );
    }
    const locationName = locations.find((l: { id: string }) => l.id === locationId)?.name ?? editing?.locationName ?? "Default location";
    const rows: [string, string][] = [
      ["Type", type?.label ?? typeId],
      ["Location", locationName],
      ["Ticket prefix", ticketPrefix ? `${ticketPrefix} — tickets like ${ticketPrefix}-104` : "—"],
      ["Wait target", `${sla} minutes`],
      ["Opening hours", qHoursLabel(hours)],
      ["Staff", staff.length ? staff.map((s) => s.name).join(", ") : "No one assigned yet"],
      ["Positions needed", String(needed)],
      ["Services", resolvedServiceNames.length ? resolvedServiceNames.join(", ") : "—"],
      ["Resources", resSel.length ? resSel.join(", ") : "—"],
      ["Journey", journey ? `${journeySource || "This queue's journey"} · ${pageSteps.length} pages` : "Not seeded yet"],
    ];
    // Step 8 — Live tab (any live queue: just published, or editing an open one).
    if (liveQueue && step >= STEPS.length) return <QueueLiveTab queue={liveQueue} name={name.trim() || liveQueue.name} locationName={locationName} />;
    const missing = reqChecks.filter((c) => !c.ok);
    return (
      <>
        <StepHead title={editing ? "Review & save" : "Review & publish"} sub={editing ? "Check the required fields and configuration, then save — edits apply to the running queue immediately." : "All required fields must be complete before the queue can publish. Publishing opens the queue right away."} />
        <div style={{ maxWidth: 560, display: "flex", flexDirection: "column", gap: 18 }}>
          <div data-testid="qwizard-required-checklist" style={{ border: `1px solid ${missing.length ? alpha(T.red1, 30) : alpha(T.green1, 30)}`, background: missing.length ? alpha(T.red1, 4) : alpha(T.green1, 5), borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: missing.length ? T.red1 : T.green1, marginBottom: 8 }}>
              {missing.length ? /* TODO(i18n): replace template-literal with t('queues.wizard.text64', { ... }) */`${missing.length} required field${missing.length === 1 ? "" : "s"} still to complete` : editing ? t('queues.wizard.text65') : t('queues.wizard.text66')}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {reqChecks.map((c) => (
                <div key={c.label} data-testid="qwizard-required-item" data-id={c.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
                  <span style={{ color: c.ok ? T.green1 : T.red1, display: "inline-flex", flexShrink: 0 }}>{c.ok ? <Icon.check size={12}/> : <Icon.x size={12}/>}</span>
                  <span style={{ color: c.ok ? T.n1 : T.red1, fontWeight: c.ok ? 500 : 600, flex: 1 }}>{c.label}</span>
                  {!c.ok ? <button onClick={() => jump(c.step)} data-testid="qwizard-required-fix" style={{ border: "none", background: "transparent", color: T.p1, fontSize: 12, fontWeight: 700, fontFamily: T.font, cursor: "pointer", padding: 0 }}>{t('queues.wizard.text67')}{" "}{c.step + 1}</button> : null}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: T.n1, marginBottom: 12 }}>{name || "Untitled queue"}</div>
            {rows.map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 13, borderBottom: `1px solid ${T.border}`, padding: "9px 0" }}>
                <span style={{ color: T.soft, flexShrink: 0 }}>{k}</span><span style={{ fontWeight: 600, color: T.n1, textAlign: "right" }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.6, padding: "10px 12px", background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8 }}>
            {editing ? t('queues.wizard.text68') : t('queues.wizard.text69')}
          </div>
        </div>
      </>
    );
  };

  return (
    <Screen rail="q-board">
      <PageHeader title={editing ? `Edit queue — ${editing.name}` : "Create queue"} subtitle={type ? `${type.label} · ${qHoursLabel(hours)}` : undefined}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("queues")}><Icon.arrow dir="left" size={11} /> {t('queues.wizard.text70')}</span>} />
      <DataSourceStrip screen="qwizard" ops={["listQueues", "listQueueTypes", "listQueueFlows", "createQueue", "updateQueue", "getQueueBoard", "sendQueueMessage"]} />
      {/* Phase track — event-wizard appearance: numbered circles + connector rules (no pills); every step is clickable back and forth. After publishing, an 8th "Live" step appears and back-navigation is disabled (the queue exists now — further changes go through Edit). */}
      <div style={{ padding: "14px 24px", background: T.white, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", overflowX: "auto", gap: 0 }}>
        {(liveQueue ? [...STEPS, "Live"] : STEPS).map((s, i, arr) => {
          const on = i === step;
          const done = !!published && i < STEPS.length;
          return [
            <button key={s} onClick={() => { if (!published) jump(i); }} data-testid={`qwizard-phase-${i}`} aria-current={on ? "step" : undefined}
              style={{ display: "inline-flex", alignItems: "center", gap: 9, border: "none", background: "transparent", cursor: published ? "default" : "pointer", fontFamily: T.font, padding: 0, flexShrink: 0 }}>
              <span style={{ width: 26, height: 26, borderRadius: 999, border: `1.5px solid ${done ? T.green1 : on ? T.p1 : T.n4}`, color: done ? T.green1 : on ? T.p1 : T.soft, background: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 700, flexShrink: 0, boxSizing: "border-box" }}>{done ? <Icon.check size={12}/> : i + 1}</span>
              <span style={{ fontSize: 13, fontWeight: on ? 700 : 500, color: on ? T.n1 : T.soft, whiteSpace: "nowrap" }}>{s}</span>
            </button>,
            i < arr.length - 1 ? <span key={s + "-rule"} aria-hidden style={{ flex: "1 0 16px", height: 1, background: T.border, margin: "0 12px" }} /> : null,
          ];
        })}
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "20px 24px" }}>
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: T.shadowSm, padding: "24px 28px", maxWidth: step === 1 ? 1400 : 860 }}>
          {body()}
          {err ? <div style={{ marginTop: 16, padding: "8px 12px", borderRadius: 8, background: alpha(T.red1, 8), border: `1px solid ${alpha(T.red1, 30)}`, color: T.red1, fontSize: 12.5 }}>{err}</div> : null}
        </div>
      </div>
      <div style={{ padding: "12px 24px", borderTop: `1px solid ${T.border}`, background: T.white, display: "flex", alignItems: "center", gap: 8 }}>
        {published ? (
          <>
            <span style={{ flex: 1 }}/>
            <Btn variant="secondary" onClick={() => nav.go("queues")} testid="qwizard-done-queues-btn">{t('queues.wizard.text71')}</Btn>
            <Btn onClick={() => nav.go("queues.console", { q: published.id })} testid="qwizard-console-btn">{t('queues.wizard.text72')}</Btn>
          </>
        ) : liveQueue && step >= STEPS.length ? (
          <>
            <Btn variant="secondary" onClick={() => nav.go("queues")}>{t('queues.wizard.text73')}</Btn>
            <span style={{ flex: 1 }}/>
            <Btn variant="secondary" onClick={back} testid="qwizard-back-btn">{t('queues.wizard.text74')}</Btn>
            <Btn onClick={() => nav.go("queues.console", { q: liveQueue.id })} testid="qwizard-console-btn">{t('queues.wizard.text72')}</Btn>
          </>
        ) : (
          <>
            <Btn variant="secondary" onClick={() => nav.go("queues")}>{draftId ? t('queues.wizard.text73') : t('queues.wizard.text75')}</Btn>
            {autosaveTarget ? (
              <span data-testid="qwizard-autosave-state" style={{ fontSize: 12, color: saveState === "error" ? T.red1 : T.soft }}>
                {saveState === "saving" ? t('queues.wizard.text76') : saveState === "saved" ? t('queues.wizard.text77') : saveState === "error" ? t('queues.wizard.text78') : t('queues.wizard.text79')}
              </span>
            ) : null}
            <span style={{ flex: 1 }}/>
            {step > 0 ? <Btn variant="secondary" onClick={back} testid="qwizard-back-btn">{t('queues.wizard.text74')}</Btn> : null}
            {step < STEPS.length - 1
              ? <Btn onClick={next} testid="qwizard-continue-btn">{t('queues.wizard.text80')}</Btn>
              : <Btn onClick={finish} disabled={saving || reqChecks.some((c) => !c.ok)} testid="qwizard-finish-btn" style={{ opacity: saving || reqChecks.some((c) => !c.ok) ? .6 : 1 }}>{saving ? (editing ? "Saving…" : "Publishing…") : editing ? t('queues.wizard.text81') : t('queues.wizard.text82')}</Btn>}
          </>
        )}
      </div>
      {copyPick ? (
        <div onClick={() => setCopyPick(false)} role="dialog" aria-label={t('queues.wizard.ariaLabel10')} style={{ position: "fixed", inset: 0, zIndex: 260, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 480, maxWidth: "94vw", maxHeight: "80vh", background: T.white, borderRadius: 14, boxShadow: T.shadowLg, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{t('queues.wizard.text83')}</div>
                <div style={{ fontSize: 11.5, color: T.soft, marginTop: 2 }}>{t('queues.wizard.text84')}</div>
              </div>
              <button onClick={() => setCopyPick(false)} aria-label={t('queues.wizard.ariaLabel11')} style={{ border: "none", background: "transparent", color: T.n2, cursor: "pointer", display: "inline-flex" }}><Icon.x size={16}/></button>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              {copyableQueues.map((q) => {
                const j = journeyOfQueue(q);
                let pgs = 0, msgs = 0;
                const walkC = (arr: QFStep[]) => arr.forEach((s) => { if (s.kind === "page") pgs++; else if (s.kind === "comms" || s.kind === "survey") msgs++; else if (s.kind === "decision") { walkC(s.yes); walkC(s.no); } });
                walkC(j?.steps ?? []);
                return (
                  <button key={q.id} onClick={() => seedCopy(q)} data-testid="qwizard-copy-queue-option" data-id={q.id}
                    style={{ textAlign: "left", padding: "11px 12px", borderRadius: 10, border: `1px solid ${T.border}`, background: T.white, cursor: "pointer", fontFamily: T.font }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1, flex: 1 }}>{q.name}</span>
                      <Pill tone={q.status === "open" ? "success" : "neutral"}>{q.status === "open" ? t('queues.wizard.text85') : q.status === "paused" ? t('queues.wizard.text86') : t('queues.wizard.text87')}</Pill>
                    </div>
                    <div style={{ fontSize: 11.5, color: T.soft, marginTop: 3 }}>{q.locationName} · {pgs} {t('queues.wizard.text31')}{" "}{pgs === 1 ? "" : t('queues.wizard.text32')} · {msgs} {t('queues.wizard.text33')}{" "}{msgs === 1 ? "" : t('queues.wizard.text32')}</div>
                  </button>
                );
              })}
              {copyableQueues.length === 0 ? <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{t('queues.wizard.text88')}</div> : null}
            </div>
          </div>
        </div>
      ) : null}
    </Screen>
  );
}

/* ── Step 8 — Live tab: the running queue's public journey link + messaging.
      Shown for a just-published queue AND when editing an open one. Board
      polls every 10s; messages go through the same awaited sendQueueMessage
      op as the console thread (no fake interactivity). ── */
function QueueLiveTab({ queue, name, locationName }: { queue: QueueSummary; name: string; locationName: string }) {
  const t = useT();
  const nav = useNav();
  useTenantPublicConfig(); // warm the public-host cache for the journey link
  const { data: board, reload } = useWfResource<QueueBoard | null>(`qboard|wiz|${queue.id}`, () => getQueueBoard(queue.id), { pollMs: 10_000 });
  const [msgTargets, setMsgTargets] = useState<QueueEntry[] | null>(null);
  const entries = board?.entries ?? [];
  const active = [
    ...entries.filter((e) => e.status === "serving"),
    ...entries.filter((e) => e.status === "called"),
    ...entries.filter((e) => e.status === "waiting").sort((a, b) => a.position - b.position),
  ];
  const journeyUrl = () => publicUrl(queue.joinCode || `q/${queue.id}`, { prefix: "join" });
  const statusPill = (e: QueueEntry) =>
    e.status === "serving" ? <Pill tone="success">{t('queues.wizard.text89')}</Pill>
    : e.status === "called" ? <Pill tone="info" dot>{t('queues.wizard.text90')}</Pill>
    : <Pill tone="neutral">{t('queues.wizard.text91')}{e.position}</Pill>;
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span aria-hidden style={{ width: 10, height: 10, borderRadius: 999, background: T.green1, boxShadow: `0 0 0 4px ${alpha(T.green1, 18)}`, flexShrink: 0 }} />
        <h2 style={{ fontSize: 19, fontWeight: 700, color: T.n1, margin: 0 }}>{name} {t('queues.wizard.text92')}</h2>
        <span data-testid="qwizard-live-pill" style={{ fontSize: 11, fontWeight: 700, color: T.green1, background: alpha(T.green1, 10), border: `1px solid ${alpha(T.green1, 30)}`, borderRadius: 999, padding: "3px 10px", textTransform: "uppercase", letterSpacing: ".05em" }}>{t('queues.wizard.text85')}</span>
      </div>
      <p style={{ fontSize: 13, color: T.soft, margin: "0 0 18px", lineHeight: 1.5 }}>{t('queues.wizard.text93')}{" "}{locationName} {t('queues.wizard.text94')}{" "}{qHoursLabel(queue.hours)}.</p>

      <div style={{ maxWidth: 620, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: T.n1, marginBottom: 3 }}>{t('queues.wizard.text95')}</div>
            <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.5, marginBottom: 10 }}>{t('queues.wizard.text96')}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <CopyableUrl url={journeyUrl()} copyLabel="Copy journey link" copiedMessage="Journey link copied" data-testid="qwizard-live-journey-link" style={{ flex: 1, minWidth: 240 }} />
              <Btn variant="secondary" icon={<Icon.external size={13}/>} onClick={() => nav.go("queue.join", { q: queue.id })} testid="qwizard-live-preview-btn">{t('queues.wizard.text97')}</Btn>
            </div>
          </div>
          {/* QR of the same journey link — generated locally from the URL above,
              so it always matches it (no separate op, nothing to keep in sync). */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center", flexShrink: 0 }}>
            <div style={{ padding: 8, border: `1px solid ${T.border}`, borderRadius: 8, background: T.white }}>
              <QrCode value={journeyUrl()} size={132} testid="qwizard-live-journey-qr" />
            </div>
            <Btn variant="secondary" size="sm" icon={<Icon.download size={12}/>} testid="qwizard-live-qr-download-btn"
              onClick={() => {
                const ok = downloadQrPng(journeyUrl(), `${(name || "queue").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-qr`);
                toast(ok
                  ? { tone: "success", title: t('queues.wizard.qrDownloaded', "QR code downloaded"), subtitle: t('queues.wizard.qrDownloadedSub', "A print-ready PNG is in your downloads.") }
                  : { tone: "danger", title: t('queues.wizard.qrDownloadFailed', "Couldn't generate the QR code") });
              }}>{t('queues.wizard.downloadQrPng', "Download PNG")}</Btn>
            <div style={{ fontSize: 11, color: T.soft, textAlign: "center", maxWidth: 148, lineHeight: 1.4 }}>{t('queues.wizard.qrScanHint', "Scan to open the journey — print it for the door or desk.")}</div>
          </div>
        </div>

        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{t('queues.wizard.text98')}{active.length}</div>
              <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{t('queues.wizard.text99')}</div>
            </div>
            <Btn icon={<Icon.msg size={13}/>} disabled={active.length === 0} onClick={() => setMsgTargets(active)} testid="qwizard-live-message-all-btn">{t('queues.wizard.text100')}</Btn>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {active.map((e) => (
              <div key={e.id} data-testid="qwizard-live-queuer" data-id={e.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: `1px solid ${T.border}`, borderRadius: 9, background: T.white }}>
                {e.ticket ? <span style={{ fontFamily: T.fontMono, fontSize: 12, fontWeight: 700, color: T.n2, flexShrink: 0 }}>{e.ticket}</span> : null}
                <span style={{ fontSize: 13, fontWeight: 600, color: T.n1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.name}{e.party > 1 ? <span style={{ color: T.soft, fontWeight: 500 }}> +{e.party - 1}</span> : null}</span>
                {statusPill(e)}
                <span style={{ fontSize: 11.5, fontFamily: T.fontMono, color: T.soft, flexShrink: 0 }}>{e.waitMins}{t('queues.wizard.text101')}</span>
                <Btn variant="secondary" size="sm" onClick={() => setMsgTargets([e])} testid="qwizard-live-message-btn">{t('queues.wizard.text102')}</Btn>
              </div>
            ))}
            {active.length === 0 ? <div style={{ padding: "16px 12px", border: `1.5px dashed ${T.n4}`, borderRadius: 9, fontSize: 12.5, color: T.soft, textAlign: "center" }}>{t('queues.wizard.text103')}</div> : null}
          </div>
        </div>

        <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.6, padding: "10px 12px", background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8 }}>
          {t('queues.wizard.text104')}</div>
      </div>
      {msgTargets ? <QueueMessageModal queueId={queue.id} targets={msgTargets} onClose={() => setMsgTargets(null)} onSent={() => { setMsgTargets(null); reload(); }} /> : null}
    </>
  );
}

function QueueMessageModal({ queueId, targets, onClose, onSent }: { queueId: string; targets: QueueEntry[]; onClose: () => void; onSent: () => void }) {
  const t = useT();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const one = targets.length === 1 ? targets[0] : null;
  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true); setErr(null);
    try {
      for (const e of targets) await sendQueueMessage(queueId, e.id, t);
      toast({ tone: "success", title: one ? `Message sent to ${one.name}` : `Message sent to ${targets.length} queuers`, subtitle: "Delivered via each customer's chosen channel (SMS / WhatsApp)." });
      onSent();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setSending(false);
    }
  };
  return (
    <div onClick={onClose} role="dialog" aria-label={one ? `Message ${one.name}` : "Message all queuers"} style={{ position: "fixed", inset: 0, zIndex: 250, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 460, maxWidth: "94vw", background: T.white, borderRadius: 14, boxShadow: T.shadowLg, padding: 22, display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: T.n1, margin: 0 }}>{one ? /* TODO(i18n): replace template-literal with t('queues.wizard.text105', { ... }) */`Message ${one.name}` : /* TODO(i18n): replace template-literal with t('queues.wizard.text106', { ... }) */`Message all queuers (${targets.length})`}</h2>
          <div style={{ fontSize: 12, color: T.soft, marginTop: 4, lineHeight: 1.5 }}>{one ? /* TODO(i18n): replace template-literal with t('queues.wizard.text107', { ... }) */`Sent to ${one.name}'s chosen channel${one.ticket ? ` — ticket ${one.ticket}` : ""}.` : t('queues.wizard.text108')}</div>
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} autoFocus placeholder={t('queues.wizard.placeholder3')}
          data-testid="qwizard-live-message-input" aria-label={t('queues.wizard.ariaLabel12')}
          style={{ width: "100%", padding: 10, border: `1px solid ${T.n4}`, borderRadius: 8, fontSize: 13.5, fontFamily: T.font, boxSizing: "border-box", resize: "vertical", minHeight: 72 }} />
        {err ? <div style={{ padding: "8px 12px", borderRadius: 8, background: alpha(T.red1, 8), border: `1px solid ${alpha(T.red1, 30)}`, color: T.red1, fontSize: 12.5 }}>{err}</div> : null}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn variant="secondary" onClick={onClose}>{t('queues.wizard.text75')}</Btn>
          <Btn onClick={() => { void send(); }} disabled={sending || !text.trim()} testid="qwizard-live-send-btn" style={{ opacity: sending || !text.trim() ? .6 : 1 }}>{sending ? t('queues.wizard.text109') : t('queues.wizard.text110')}</Btn>
        </div>
      </div>
    </div>
  );
}
